<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import { useUserStore } from '@/stores/user';
import { Button, Divider, Heading, LayoutGap, Loader, Modal, Prose, useConfirmStore, vTooltip } from '@dabble/quillui';
import { format } from 'date-fns';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AddCoupon from '../components/AddCoupon.vue';
import Discount from '../components/Discount.vue';
import PaymentMethods from '../components/PaymentMethods.vue';
import Price from '../components/Price.vue';
import BillingPlansView from '../plans/BillingPlansView.vue';
import CancelSubscriptionOptions from './components/CancelSubscriptionOptions.vue';
import Invoices from './components/Invoices.vue';
import PauseSubscriptionOptions from './components/PauseSubscriptionOptions.vue';
import SubscriptionBadge from './components/SubscriptionBadge.vue';
import SubscriptionMessage from './components/SubscriptionMessage.vue';
import UpcomingInvoice from './components/UpcomingInvoice.vue';

const { showToast } = useToast();
const router = useRouter();
const route = useRoute();
const billingStore = useBillingStore();
const userStore = useUserStore();
const confirmStore = useConfirmStore();
const { t } = useI18n();

const actionDisabled = ref(false);
const showPauseModal = ref(false);
const showChangePlanModal = ref(false);
const showCancelModal = ref(false);

/** True until initial user + billing fetch completes — avoids flashing "no plan" before we know state. */
const portalHydrating = ref(true);

const subscription = computed(() => billingStore.primarySubscription);
const plan = computed(() => subscription.value?.plan);
const isYearlyPlan = computed(() => plan.value?.interval === 'year');

const isPausedSubscription = computed(() => billingStore.isPausedSubscription);
const isPauseLocked = computed(() => billingStore.isPauseLocked);
const pauseLockedUntilFormatted = computed(() => {
  const ts = billingStore.pauseLockedUntil;
  return ts ? format(new Date(ts * 1000), 'PP') : '';
});
const isTrialing = computed(() => billingStore.isTrialing);
const isActivePaid = computed(() => billingStore.isActivePaid);
const isSubscribedDuringTrial = computed(() => billingStore.isSubscribedDuringTrial);
const hasStripeCustomer = computed(() => billingStore.hasStripeCustomer);

/** Stripe customer exists but no active/trialing subscription row (or only canceled). */
const trialAvailable = computed(() => !subscription.value && billingStore.hasTrialAvailable);

function goToPlans() {
  showChangePlanModal.value = true;
}

watch(
  () => route.query.changePlan,
  (value) => {
    if (value) showChangePlanModal.value = true;
  },
  { immediate: true },
);

watch(showChangePlanModal, (open) => {
  if (!open && route.query.changePlan) {
    router.replace({ name: 'portal' });
  }
});

function goToExitSurvey() {
  showCancelModal.value = true;
}

function closePauseModal() {
  showPauseModal.value = false;
}

async function onStartTrialClick() {
  actionDisabled.value = true;
  try {
    if (!hasStripeCustomer.value) {
      const stripeId = await billingStore.createCustomer({
        uid: userStore.user.uid ?? '',
        email: userStore.user.email ?? '',
        name: userStore.user.name ?? '',
        withTrial: true,
      });
      if (!stripeId) return;
      await billingStore.fetchBillingOverview();
      return;
    }
    await billingStore.startTrialSubscription();
  } finally {
    actionDisabled.value = false;
  }
}

async function onSubscribeNowEnsureCustomer() {
  if (!hasStripeCustomer.value) {
    const stripeId = await billingStore.createStripeCustomerOnly({
      uid: userStore.user.uid ?? '',
      email: userStore.user.email ?? '',
      name: userStore.user.name ?? '',
    });
    if (!stripeId) return;
  }
  goToPlans();
}

async function switchToAnnual() {
  actionDisabled.value = true;
  showToast('info', t('billing_finding_annual'));
  try {
    await billingStore.fetchBillingOverview();

    const offering = billingStore.offerings;
    const plansMap = offering?.plans?.plans;
    const productId = plan.value?.product?.id ?? plan.value?.id;
    const currentProduct = productId ? plansMap?.[productId as string] : undefined;
    const annualPlanId = currentProduct?.year?.id;

    if (annualPlanId) {
      billingStore.product = currentProduct;
      billingStore.selectedPlan = currentProduct?.year ?? null;
      billingStore.selectedPlanId = annualPlanId;
      billingStore.selection = 'year';
      router.push('/billing/checkout');
    } else {
      throw new Error('Annual plan not found');
    }
  } catch (error: unknown) {
    logError('Failed to switch to annual:', error);
    showToast('danger', t('billing_annual_not_found'));
  } finally {
    actionDisabled.value = false;
  }
}

async function confirmAndCancelSubscription(when: 'now' | 'end_of_period') {
  const isTrial = billingStore.isTrialing;
  const titleKey = isTrial ? 'billing_cancel_confirm_trial_title' : 'billing_cancel_confirm_subscription_title';
  const bodyKey = isTrial ? 'billing_cancel_confirm_trial_body' : 'billing_cancel_confirm_subscription_body';
  const cancelKey = isTrial ? 'billing_cancel_trial' : 'billing_cancel_subscription';

  const confirmed = await confirmStore.confirm(t(titleKey), t(bodyKey), {
    cancel: { text: t('billing_keep_it') },
    confirm: { text: t(cancelKey), variant: 'danger' },
  });

  if (!confirmed) return;

  actionDisabled.value = true;
  try {
    const ok = await billingStore.cancelSubscription({
      when: when === 'now' ? 'now' : undefined,
    });
    if (ok) {
      showToast('success', t('billing_cancel_success'));
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Could not cancel subscription.';
    logError('Failed to cancel subscription:', error);
    showToast('danger', message);
  } finally {
    actionDisabled.value = false;
  }
}

async function unpauseFromPortal() {
  actionDisabled.value = true;
  try {
    const ok = await billingStore.unpauseBillingSubscription();
    if (ok) {
      showToast('success', t('billing_resumed'));
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Could not resume subscription.';
    logError('Failed to unpause subscription:', error);
    showToast('danger', message);
  } finally {
    actionDisabled.value = false;
  }
}

async function undoCancellation() {
  actionDisabled.value = true;
  try {
    const ok = await billingStore.resumeSubscription();
    if (ok) showToast('success', t('billing_undo_cancel_success'));
  } finally {
    actionDisabled.value = false;
  }
}

function handleSubscriptionPaused() {
  showPauseModal.value = false;
}

onMounted(async () => {
  try {
    if (userStore.user.uid) {
      await userStore.fetchUser(userStore.user.uid);
      await billingStore.fetchBillingOverview();
    }
  } finally {
    portalHydrating.value = false;
  }
});
</script>

<template>
  <LayoutGap direction="column" size="small" fill class="billing-portal">
    <LayoutGap direction="column" size="xxsmall" fill>
      <Heading :level="1" no-margin>{{ t('billing_title') }}</Heading>
      <Divider variant="secondary" size="tiny" />
    </LayoutGap>

    <LayoutGap
      v-if="portalHydrating"
      direction="column"
      size="small"
      fill
      align="center"
      class="billing-portal__loader-wrap"
    >
      <Loader />
    </LayoutGap>

    <template v-else>
      <Heading :level="2" no-margin>{{ t('billing_plan_current') }}</Heading>
      <Divider variant="secondary" size="tiny" />

      <LayoutGap direction="row" align="space-between" size="small" fill wrap class="billing-portal__subscription-row">
        <LayoutGap v-if="subscription && plan" direction="column" size="small" fill class="billing-portal__plan">
          <SubscriptionBadge :subscription="subscription" />
          <Heading :level="3">{{ plan?.name || plan?.product?.name }}</Heading>
          <Prose v-if="plan?.description" variant="secondary">
            {{ plan.description }}
          </Prose>
          <Price :plan="plan" />
          <SubscriptionMessage
            :subscription="subscription"
            :disabled="actionDisabled"
            @undo-cancellation="undoCancellation"
          />
        </LayoutGap>

        <LayoutGap v-else direction="column" size="small" fill class="billing-portal__plan">
          <Heading :level="3">{{ t('billing_no_active_plan') }}</Heading>
        </LayoutGap>

        <LayoutGap direction="column" size="small" class="billing-portal__actions">
          <!-- 3. Paused -->
          <template v-if="isPausedSubscription && subscription && plan">
            <span
              v-tooltip="isPauseLocked ? t('billing_pause_lock_tooltip', { date: pauseLockedUntilFormatted }) : ''"
              class="billing-portal__lock-wrap"
            >
              <Button
                variant="primary"
                finish="filled"
                :disabled="actionDisabled || isPauseLocked"
                @click="unpauseFromPortal"
              >
                {{ t('billing_pause_undo') }}
              </Button>
            </span>
            <Button variant="primary" finish="filled" :disabled="actionDisabled" @click="goToExitSurvey">
              {{ t('billing_cancel_subscription', 'Cancel Subscription') }}
            </Button>
          </template>

          <!-- 2. Trialing without active subscription (no payment method, or cancel pending) -->
          <template v-else-if="isTrialing && !isSubscribedDuringTrial && subscription && plan">
            <Button variant="primary" finish="filled" :disabled="actionDisabled" @click="goToPlans">
              {{ t('billing_subscribe_now') }}
            </Button>
            <Button
              variant="primary"
              finish="filled"
              :disabled="actionDisabled"
              @click="confirmAndCancelSubscription('now')"
            >
              {{ t('billing_cancel_trial') }}
            </Button>
          </template>

          <!-- 1. Active paid (or trialing with payment method on file) -->
          <template v-else-if="(isActivePaid || isSubscribedDuringTrial) && subscription && plan">
            <template v-if="plan?.amount !== 0">
              <Button variant="primary" finish="filled" :disabled="actionDisabled" @click="goToPlans">
                {{ t('billing_change_plan') }}
              </Button>
              <LayoutGap
                v-if="plan?.interval !== 'year'"
                direction="column"
                size="xtiny"
                class="billing-portal__annual-switch"
              >
                <Button variant="secondary" finish="outlined" :disabled="actionDisabled" @click="switchToAnnual">
                  {{ t('billing_annual_switch') }}
                </Button>
                <Prose size="xsmall" variant="secondary" class="billing-portal__annual-note">
                  {{ t('billing_annual_switch_message') }}
                </Prose>
              </LayoutGap>
              <span v-tooltip="isYearlyPlan ? t('billing_pause_yearly_tooltip') : ''" class="billing-portal__lock-wrap">
                <Button
                  variant="secondary"
                  finish="outlined"
                  :disabled="actionDisabled || isYearlyPlan"
                  @click="showPauseModal = true"
                >
                  {{ t('billing_pause_subscription') }}
                </Button>
              </span>
              <Button
                v-if="!subscription.cancel_at_period_end"
                variant="danger"
                finish="outlined"
                :disabled="actionDisabled"
                @click="goToExitSurvey"
              >
                {{ t('billing_cancel_subscription', 'Cancel Subscription') }}
              </Button>
            </template>
            <Prose v-else variant="secondary">{{ t('billing_lifetime_plan') }}</Prose>
          </template>

          <!-- Past due -->
          <template v-else-if="subscription?.status === 'past_due' && plan">
            <Button variant="primary" finish="filled" :disabled="actionDisabled" @click="goToPlans">
              {{ t('billing_change_plan') }}
            </Button>
            <span v-tooltip="isYearlyPlan ? t('billing_pause_yearly_tooltip') : ''" class="billing-portal__lock-wrap">
              <Button
                variant="secondary"
                finish="outlined"
                :disabled="actionDisabled || isYearlyPlan"
                @click="showPauseModal = true"
              >
                {{ t('billing_pause_subscription') }}
              </Button>
            </span>
            <Button
              v-if="!subscription.cancel_at_period_end"
              variant="danger"
              finish="outlined"
              :disabled="actionDisabled"
              @click="goToExitSurvey"
            >
              {{ t('billing_cancel_subscription', 'Cancel Subscription') }}
            </Button>
          </template>

          <!-- 4. Stripe customer, no subscription -->
          <template v-else-if="hasStripeCustomer && !subscription">
            <Button
              v-if="trialAvailable"
              variant="primary"
              finish="filled"
              :disabled="actionDisabled"
              @click="onStartTrialClick"
            >
              {{ t('billing_trial_start') }}
            </Button>
            <Button variant="primary" finish="filled" :disabled="actionDisabled" @click="onSubscribeNowEnsureCustomer">
              {{ t('billing_subscribe_now') }}
            </Button>
          </template>

          <!-- 5. No Stripe customer -->
          <template v-else-if="!hasStripeCustomer">
            <Button variant="primary" finish="filled" :disabled="actionDisabled" @click="onStartTrialClick">
              {{ t('billing_trial_start') }}
            </Button>
            <Button variant="primary" finish="filled" :disabled="actionDisabled" @click="onSubscribeNowEnsureCustomer">
              {{ t('billing_subscribe_now') }}
            </Button>
          </template>

          <!-- Fallback -->
          <template v-else-if="subscription && plan">
            <Button variant="primary" finish="filled" :disabled="actionDisabled" @click="goToPlans">
              {{ t('billing_change_plan') }}
            </Button>
          </template>
        </LayoutGap>
      </LayoutGap>

      <LayoutGap v-if="hasStripeCustomer" direction="column" size="small" fill class="billing-portal__stripe-extras">
        <Discount />
        <UpcomingInvoice />
        <AddCoupon
          :disabled="actionDisabled"
          :link="isTrialing || !!subscription?.cancel_at_period_end"
          class="billing-portal__add-coupon"
        />
      </LayoutGap>

      <Divider variant="secondary" size="small" />

      <template v-if="hasStripeCustomer">
        <LayoutGap direction="column" size="xsmall" fill class="billing-portal__section">
          <Heading :level="2" no-margin>{{ t('billing_payment_method') }}</Heading>
          <PaymentMethods />
        </LayoutGap>

        <Divider variant="secondary" size="small" />

        <LayoutGap direction="column" size="xsmall" fill class="billing-portal__section">
          <Heading :level="2" no-margin>{{ t('billing_invoice_history') }}</Heading>
          <Invoices />
        </LayoutGap>
      </template>

      <Modal v-model="showPauseModal" size="small" :title="t('billing_pause_subscription')" @close="closePauseModal">
        <PauseSubscriptionOptions @pause-subscription="handleSubscriptionPaused" @back-to-billing="closePauseModal" />
      </Modal>

      <Modal v-model="showChangePlanModal" size="xlarge" :title="t('billing_change_plan')">
        <BillingPlansView />
      </Modal>

      <Modal v-model="showCancelModal" size="large" :title="t('billing_cancel_subscription', 'Cancel Subscription')">
        <CancelSubscriptionOptions
          @cancel="showCancelModal = false"
          @subscription-cancelled="showCancelModal = false"
        />
      </Modal>
    </template>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.billing-portal {
  max-width: 60rem;
  margin-right: auto;
  padding-bottom: 2rem;

  &__loader-wrap {
    min-height: 8rem;
    justify-content: center;
  }

  &__subscription-row {
    :deep(.layout-gap) {
      min-width: 0;
    }
  }

  &__plan {
    flex: 1 1 18rem;
    min-width: 0;
  }

  &__actions {
    flex: 0 1 16rem;
    min-width: 10rem;
  }

  // Wraps a disabled button so v-tooltip still fires on hover
  // (disabled buttons don't emit pointer events directly).
  &__lock-wrap {
    display: inline-block;
  }

  &__add-coupon {
    margin-top: space('small');
  }

  &__section {
    max-width: 31.25rem;
  }
}
</style>
